import axios from 'axios'
import { defineStore } from 'pinia'
import type { DocumentTreeItem } from '../types/tree.types'
import { getApiTreeChildren } from '../utils/normalizeApiTree'
import { BASE_URL } from '@/environment/environment'

const RZN_SECTIONS_URL = `${BASE_URL}/api/qualitycontrol/standard/`

function toChoice(item: { id: number | string; name?: string; label?: string; children?: unknown[]; childrens?: unknown[] }): DocumentTreeItem {
  const nodes = getApiTreeChildren(item)
  return {
    id: item.id,
    label: item.label ?? item.name ?? String(item.id),
    children: nodes.length ? nodes.map(n => toChoice(n as typeof item)) : [],
  }
}

function sortSections(items: DocumentTreeItem[]): DocumentTreeItem[] {
  return items
    .sort((a, b) => {
      const numA = Number.parseFloat((a.label || '').match(/\d+(\.\d+)?/)?.[0] || '0')
      const numB = Number.parseFloat((b.label || '').match(/\d+(\.\d+)?/)?.[0] || '0')
      return numA - numB
    })
    .map(item => ({
      ...item,
      children: item.children?.length ? sortSections(item.children) : [],
    }))
}

function flattenSectionChoices(items: DocumentTreeItem[]): { title: string; value: string }[] {
  const result: { title: string; value: string }[] = []
  function walk(nodes: DocumentTreeItem[], prefix = '') {
    for (const n of nodes) {
      result.push({ title: prefix + (n.label || String(n.id)), value: String(n.id) })
      if (n.children?.length) walk(n.children, `${prefix}  `)
    }
  }
  walk(items)
  return result
}

interface RznSectionsResponse {
  next?: string | null
  previous?: string | null
  count?: number
  results?: unknown[]
}

/** Глубина дерева стандартов: тип элемента, а не числовой уровень */
export type RznTreeDepth = 'section' | 'requirement' | 'criterion' | 'point'

function buildFetchKey(standardsKey: string, depth: RznTreeDepth): string {
  return `${depth}:${standardsKey}`
}

let inflightRequest: Promise<void> | null = null
let inflightKey = ''
let requestSeq = 0

export const useRznSectionsStore = defineStore('rznSections', {
  state: () => ({
    sectionsTree: [] as DocumentTreeItem[],
    loading: false,
    lastStandardsKey: '' as string,
  }),

  getters: {
    sectionSelectOptions(state): { title: string; value: string }[] {
      return flattenSectionChoices(state.sectionsTree || [])
    },
  },

  actions: {
    async fetchSections(params: { standards?: Array<string | number>; depth?: RznTreeDepth } = {}): Promise<void> {
      const standards = (params.standards || []).filter(x => x !== null && x !== undefined && String(x).trim() !== '')
      const standardsKey = standards.map(String).sort().join(',')
      const depth = params.depth ?? 'section'
      const fetchKey = buildFetchKey(standardsKey, depth)

      if (inflightRequest && inflightKey === fetchKey) {
        return inflightRequest
      }

      const requestId = ++requestSeq

      inflightKey = fetchKey
      inflightRequest = (async () => {
        this.loading = true
        try {
          const collected: DocumentTreeItem[] = []
          let cursor: string | undefined

          for (let guard = 0; guard < 50; guard++) {
            const query: Record<string, string> = {
              depth,
            }
            if (cursor) {
              query.cursor = cursor
            }
            if (standards.length) {
              query.standards = standards.join(',')
            }

            const { data } = await axios.get<RznSectionsResponse>(RZN_SECTIONS_URL, { params: query })
            const batch = (data.results || []).map((r: any) => toChoice(r))

            collected.push(...batch)

            const next = data.next ?? null
            if (!next) {
              break
            }

            try {
              const nextUrl = new URL(next)
              cursor = nextUrl.searchParams.get('cursor') || undefined
            } catch {
              break
            }
          }

          if (requestId !== requestSeq) {
            return
          }

          this.sectionsTree = sortSections(collected)
          this.lastStandardsKey = standardsKey
        } catch (e) {
          console.error('[rznSections] fetchSections:', e)
          if (requestId === requestSeq) {
            this.lastStandardsKey = this.sectionsTree.length ? this.lastStandardsKey : ''
          }
          throw e
        } finally {
          if (requestId === requestSeq) {
            this.loading = false
          }
          if (inflightKey === fetchKey) {
            inflightRequest = null
            inflightKey = ''
          }
        }
      })()

      return inflightRequest
    },

    reset() {
      requestSeq += 1
      inflightRequest = null
      inflightKey = ''
      this.sectionsTree = []
      this.lastStandardsKey = ''
      this.loading = false
    },
  },
})
